import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruiterAPI } from '@/services/api';
import { Plus, Briefcase, Edit2, Archive, Trash2, Eye, History as HistoryIcon } from 'lucide-react';
import ConfirmChargeDialog from '@/components/shared/ConfirmChargeDialog';
import Pagination from '@/components/shared/Pagination';

export default function RecruiterJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showCreate, setShowCreate] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // {type: 'archive'|'delete', job}
  const [actionLoading, setActionLoading] = useState(false);

  const load = async (p = page, ps = pageSize) => {
    try {
      const effectiveStatus = statusFilter || 'active';
      const d = await recruiterAPI.getJobs({
        skip: (p - 1) * ps,
        limit: ps,
        status: effectiveStatus,
        job_type: typeFilter || undefined,
        sort_order: sortOrder,
      });
      setJobs(d.jobs);
      setTotal(d.total);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { setPage(1); load(1, pageSize); }, [statusFilter, typeFilter, sortOrder, pageSize]);
  useEffect(() => { load(page, pageSize); }, [page]);

  const doArchive = async () => {
    if (!confirm?.job) return;
    setActionLoading(true);
    try { await recruiterAPI.archiveJob(confirm.job.id); setConfirm(null); setPage(1); load(1, pageSize); }
    catch (e) { alert(e?.response?.data?.detail || 'Archive failed'); }
    finally { setActionLoading(false); }
  };

  const doDelete = async () => {
    if (!confirm?.job) return;
    setActionLoading(true);
    try { await recruiterAPI.deleteJob(confirm.job.id); setConfirm(null); setPage(1); load(1, pageSize); }
    catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
    finally { setActionLoading(false); }
  };

  return (
    <div data-testid="recruiter-jobs">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Jobs</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">{total} total postings</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/recruiter/jobs/history')} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-700 text-slate-600 dark:text-zinc-300 rounded-lg text-sm" data-testid="open-job-history-btn">
            <HistoryIcon className="w-4 h-4" /> Job History
          </button>
          <button onClick={() => { setEditJob(null); setShowCreate(true); }} className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm" data-testid="create-job-btn"><Plus className="w-4 h-4" />Create Job</button>
        </div>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300" data-testid="jobs-status-filter">
          <option value="">Active</option>
          <option value="active">Active</option>
          <option value="closed">Closed</option>
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300">
          <option value="">All Types</option><option value="permanent">Permanent</option><option value="contractual">Contractual</option><option value="freelancing">Freelancing</option>
        </select>
        <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300">
          <option value="desc">Latest First</option><option value="asc">Oldest First</option>
        </select>
      </div>

      {loading ? <p className="text-slate-500 dark:text-zinc-500">Loading...</p> : jobs.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center"><Briefcase className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" /><p className="text-slate-500 dark:text-zinc-500">No jobs yet</p></div>
      ) : (
        <>
          <div className="space-y-3">
            {jobs.map(j => (
              <div key={j.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:border-slate-300 dark:hover:border-zinc-700 transition-colors" onClick={() => navigate(`/recruiter/jobs/${j.id}`)} data-testid={`job-row-${j.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-slate-900 dark:text-white font-medium truncate">{j.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${j.status === 'active' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : j.status === 'archived' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-500'}`}>{j.status}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400">{j.job_type}</span>
                  </div>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs mt-1 line-clamp-1">{j.description}</p>
                  <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">{new Date(j.created_at).toLocaleDateString()} {j.compensation_min && `· $${j.compensation_min}-$${j.compensation_max}`}</p>
                  {j.match_stats && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11.5px]" data-testid={`job-stats-${j.id}`}>
                      <span className="inline-flex items-center gap-1 text-slate-600 dark:text-zinc-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                        Matched <span className="font-medium text-slate-900 dark:text-white">{j.match_stats.total}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        In process <span className="font-medium">{j.match_stats.in_process}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Selected <span className="font-medium">{j.match_stats.selected}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Rejected <span className="font-medium">{j.match_stats.rejected}</span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/recruiter/jobs/${j.id}`); }} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid={`view-job-${j.id}`}><Eye className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); setEditJob(j); setShowCreate(true); }} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white" data-testid={`edit-job-${j.id}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); setConfirm({ type: 'archive', job: j }); }} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-amber-700 dark:hover:text-amber-300" data-testid={`archive-job-${j.id}`} title="Archive"><Archive className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); setConfirm({ type: 'delete', job: j }); }} className="p-2 text-slate-500 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-job-${j.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
          {total > pageSize && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              testIdPrefix="recruiter-jobs-pg"
            />
          )}
        </>
      )}

      {showCreate && <JobModal job={editJob} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); setPage(1); load(1, pageSize); }} />}

      {confirm && (
        <ConfirmDialog
          title={confirm.type === 'archive' ? 'Archive this job?' : 'Delete this job?'}
          message={confirm.type === 'archive'
            ? 'Archived jobs are hidden from the active list but can be searched and restored from Job History.'
            : 'This will permanently delete the job and ALL associated matches, screenings, and interviews. This cannot be undone.'}
          confirmLabel={confirm.type === 'archive' ? 'Archive' : 'Delete permanently'}
          danger={confirm.type === 'delete'}
          loading={actionLoading}
          onCancel={() => setConfirm(null)}
          onConfirm={confirm.type === 'archive' ? doArchive : doDelete}
        />
      )}
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel, danger, loading, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onCancel} data-testid="confirm-dialog">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{title}</h3>
        <p className="text-slate-500 dark:text-zinc-400 text-sm mb-5">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm" data-testid="confirm-cancel">Cancel</button>
          <button onClick={onConfirm} disabled={loading} data-testid="confirm-action"
            className={`flex-1 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${danger ? 'bg-red-500 hover:bg-red-400 text-white' : 'bg-amber-400 hover:bg-amber-300 text-black'}`}>
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function JobModal({ job, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: job?.title || '', description: job?.description || '', requirements: job?.requirements || '',
    job_type: job?.job_type || 'permanent', compensation_min: job?.compensation_min || '', compensation_max: job?.compensation_max || '',
    status: job?.status || 'active', ai_screening_enabled: job?.ai_screening_enabled || false, min_match_percentage: job?.min_match_percentage ?? 70,
    job_fit_threshold: job?.job_fit_threshold ?? null,
    recording_addon: job?.recording_addon || false,
    ai_evaluation_addon: job?.ai_evaluation_addon || false,
    candidate_selection_mode: job?.candidate_selection_mode || 'selective',
    staffing_filter_enabled: job?.staffing_filter_enabled || false,
    preferred_staffing_org_ids: job?.preferred_staffing_org_ids || [],
    max_notice_period_days: job?.max_notice_period_days ?? '',
    auto_interview_budget_mp: job?.auto_interview_budget_mp ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pricing, setPricing] = useState(null);
  const [balance, setBalance] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reopenPrompt, setReopenPrompt] = useState(null); // { count, newThreshold, prevThreshold }
  const [staffingOrgs, setStaffingOrgs] = useState([]);

  React.useEffect(() => {
    (async () => {
      try {
        const { staffingAPI } = await import('@/services/staffingAPI');
        const r = await staffingAPI.publicStaffingOrgs();
        setStaffingOrgs(r.orgs || []);
      } catch {/* ignore */}
    })();
  }, []);

  React.useEffect(() => {
    if (job) return; // editing existing — no charge
    (async () => {
      try {
        const w = await (await import('@/services/api')).walletAPI.me();
        setPricing(w.pricing);
        setBalance(w.balance);
      } catch {/* ignore */}
    })();
  }, [job]);

  const cost = !job && pricing
    ? (pricing.job_post || 0) + (form.recording_addon ? (pricing.recording_addon || 0) : 0) + (form.ai_evaluation_addon ? (pricing.ai_eval_addon || 0) : 0)
    : 0;

  const buildData = () => {
    const jobFitEnabled = !(form.job_fit_threshold === null || form.job_fit_threshold === '' || Number(form.job_fit_threshold) <= 0);
    return {
      ...form,
      compensation_min: form.compensation_min ? Number(form.compensation_min) : null,
      compensation_max: form.compensation_max ? Number(form.compensation_max) : null,
      min_match_percentage: Number(form.min_match_percentage ?? 70),
      // null = "no auto-decision" (backend keeps the field null / treats 0 as disabled).
      job_fit_threshold: jobFitEnabled ? Number(form.job_fit_threshold) : null,
      // Bug #2 — Auto-decide implies AI Screening must be enabled so Jay can interview.
      ai_screening_enabled: jobFitEnabled ? true : Boolean(form.ai_screening_enabled),
      preferred_staffing_org_ids: form.staffing_filter_enabled ? (form.preferred_staffing_org_ids || []) : [],
      max_notice_period_days: form.max_notice_period_days === '' || form.max_notice_period_days === null ? null : Number(form.max_notice_period_days),
      auto_interview_budget_mp: (form.candidate_selection_mode === 'auto' && form.auto_interview_budget_mp !== '' && form.auto_interview_budget_mp !== null)
        ? Math.max(0, Number(form.auto_interview_budget_mp))
        : null,
    };
  };

  const doSave = async ({ reopenRejected = false } = {}) => {
    setSaving(true);
    setError('');
    try {
      const data = buildData();
      if (job) await recruiterAPI.updateJob(job.id, data, { reopenRejected });
      else await recruiterAPI.createJob(data);
      window.dispatchEvent(new Event('mu-wallet-changed'));
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!job) {
      setConfirmOpen(true);
      return;
    }
    // Editing an existing job — check if threshold was lowered, and if so, count candidates that qualify.
    const prev = Number(job.min_match_percentage ?? 70);
    const next = Number(form.min_match_percentage);
    if (Number.isFinite(next) && next < prev) {
      try {
        const preview = await recruiterAPI.previewReopen(job.id, next);
        if (preview.eligible > 0) {
          setReopenPrompt({ count: preview.eligible, prevThreshold: prev, newThreshold: next });
          return;
        }
      } catch { /* if the preview fails, fall through to plain save */ }
    }
    doSave().catch(() => { /* noop */ });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-xl max-h-[85vh] overflow-auto p-6" onClick={e => e.stopPropagation()} data-testid="job-modal">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{job ? 'Edit Job' : 'Create Job'}</h2>
        {!job && pricing && <p className="text-xs text-slate-500 dark:text-zinc-500 mb-4">Posting cost: <span className="text-amber-700 dark:text-amber-300 font-medium" data-testid="post-cost-preview">{cost} MP</span> · Your balance: <span className="text-emerald-700 dark:text-emerald-300">{balance} MP</span></p>}
        {error && <div className="bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg mb-3 text-sm">{error}</div>}
        <form onSubmit={save} className="space-y-4">
          <Inp label="Title" value={form.title} onChange={v => setForm({...form, title: v})} required />
          <Inp label="Description" value={form.description} onChange={v => setForm({...form, description: v})} textarea required />
          <Inp label="Requirements" value={form.requirements} onChange={v => setForm({...form, requirements: v})} textarea required />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Job Type</label>
              <select value={form.job_type} onChange={e => setForm({...form, job_type: e.target.value})} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white">
                <option value="permanent">Permanent</option><option value="contractual">Contractual</option><option value="freelancing">Freelancing</option>
              </select>
            </div>
            {job && <div>
              <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white">
                <option value="active">Active</option><option value="closed">Closed</option><option value="archived">Archived</option>
              </select>
            </div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Min Compensation" value={form.compensation_min} onChange={v => setForm({...form, compensation_min: v})} type="number" />
            <Inp label="Max Compensation" value={form.compensation_max} onChange={v => setForm({...form, compensation_max: v})} type="number" />
          </div>

          {/* Notice Period requirement */}
          <div>
            <Inp label="Max notice period (days, optional)" value={form.max_notice_period_days} onChange={v => setForm({...form, max_notice_period_days: v})} type="number" />
            <p className="text-slate-500 dark:text-zinc-500 text-[11px] mt-1">Leave blank for &quot;no limit&quot;. Candidates with notice longer than this won&apos;t be matched.</p>
          </div>

          {/* ===== Match & AI thresholds ===== */}
          <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-3 space-y-4" data-testid="thresholds-section">
            <p className="text-slate-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Match & AI thresholds</p>

            {/* Minimum matching % */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-slate-600 dark:text-zinc-400">Minimum profile match %</label>
                <span className="text-xs font-medium text-slate-900 dark:text-white tabular-nums" data-testid="min-match-value">{Number(form.min_match_percentage)}%</span>
              </div>
              <input
                type="range" min="0" max="100" step="1"
                value={Number(form.min_match_percentage)}
                onChange={e => setForm({...form, min_match_percentage: Number(e.target.value)})}
                className="w-full accent-emerald-500"
                data-testid="min-match-slider"
              />
              <p className="text-slate-500 dark:text-zinc-500 text-[11px] mt-1">Candidates whose AI profile-match score is below this are hidden from your pipeline.</p>
            </div>

            {/* AI Job-fit auto-decision threshold */}
            <div className="border-t border-slate-200 dark:border-zinc-800 pt-3">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.job_fit_threshold !== null && form.job_fit_threshold !== '' && Number(form.job_fit_threshold) > 0}
                  onChange={e => setForm({...form, job_fit_threshold: e.target.checked ? 70 : null})}
                  className="mt-0.5 accent-emerald-500"
                  data-testid="job-fit-toggle"
                />
                <div className="flex-1">
                  <p className="text-slate-900 dark:text-white text-sm">Auto-decide after AI interview</p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs">When the AI Agent finishes interviewing a candidate, automatically mark them <span className="text-emerald-700 dark:text-emerald-300">Selected</span> if their job-fit score meets this threshold — otherwise <span className="text-red-600 dark:text-red-400">Rejected</span>. Recruiters can always override manually.</p>
                </div>
              </label>
              {form.job_fit_threshold !== null && form.job_fit_threshold !== '' && Number(form.job_fit_threshold) > 0 && (
                <div className="mt-3 pl-6">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-slate-600 dark:text-zinc-400">Job-fit threshold</label>
                    <span className="text-xs font-medium text-slate-900 dark:text-white tabular-nums" data-testid="job-fit-value">{Number(form.job_fit_threshold)}%</span>
                  </div>
                  <input
                    type="range" min="1" max="100" step="1"
                    value={Number(form.job_fit_threshold)}
                    onChange={e => setForm({...form, job_fit_threshold: Number(e.target.value)})}
                    className="w-full accent-emerald-500"
                    data-testid="job-fit-slider"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preferred Staffing Partners */}
          <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-3" data-testid="staffing-partner-section">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.staffing_filter_enabled} onChange={e => setForm({...form, staffing_filter_enabled: e.target.checked})} className="mt-0.5 accent-emerald-500" data-testid="staffing-filter-checkbox" />
              <div className="flex-1">
                <p className="text-slate-900 dark:text-white text-sm">Source only from preferred staffing partners</p>
                <p className="text-slate-500 dark:text-zinc-500 text-xs">Independent candidates are still matched. Only selected staffing orgs supply matching staffing-managed candidates.</p>
              </div>
            </label>
            {form.staffing_filter_enabled && (
              <div className="mt-3 max-h-40 overflow-auto bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded p-2 space-y-1.5" data-testid="staffing-orgs-picker">
                {staffingOrgs.length === 0 && <p className="text-slate-500 dark:text-zinc-500 text-xs">No approved staffing orgs yet.</p>}
                {staffingOrgs.map(o => (
                  <label key={o.id} className="flex items-center gap-2 text-xs text-slate-600 dark:text-zinc-300 cursor-pointer hover:text-slate-900 dark:hover:text-white">
                    <input type="checkbox" checked={form.preferred_staffing_org_ids.includes(o.id)}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...form.preferred_staffing_org_ids, o.id]
                          : form.preferred_staffing_org_ids.filter(x => x !== o.id);
                        setForm({...form, preferred_staffing_org_ids: next});
                      }}
                      className="accent-emerald-500"
                      data-testid={`staffing-org-${o.id}`} />
                    {o.name} <span className="text-slate-500 dark:text-zinc-600">@{o.domain}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {!job && (
            <div className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg p-3 space-y-3" data-testid="addons-section">
              <p className="text-slate-500 dark:text-zinc-400 text-xs uppercase tracking-wider">Optional add-ons (extra MP)</p>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={form.recording_addon} onChange={e => setForm({...form, recording_addon: e.target.checked})} className="mt-0.5 accent-emerald-500" data-testid="recording-addon-checkbox" />
                <div>
                  <p className="text-slate-900 dark:text-white text-sm">Candidate recordings as-is <span className="text-amber-700 dark:text-amber-300 text-xs ml-1">+{pricing?.recording_addon || 5} MP</span></p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs">Download each candidate&apos;s video/audio recording directly.</p>
                </div>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={form.ai_evaluation_addon} onChange={e => setForm({...form, ai_evaluation_addon: e.target.checked})} className="mt-0.5 accent-emerald-500" data-testid="ai-eval-addon-checkbox" />
                <div>
                  <p className="text-slate-900 dark:text-white text-sm">AI evaluation summary <span className="text-amber-700 dark:text-amber-300 text-xs ml-1">+{pricing?.ai_eval_addon || 5} MP</span></p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs">AI assesses the interview recording and returns a brief + score per candidate.</p>
                </div>
              </label>
              <div className="border-t border-slate-200 dark:border-zinc-800 pt-3">
                <p className="text-slate-500 dark:text-zinc-400 text-xs mb-1.5">Candidate journey kickoff</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm({...form, candidate_selection_mode: 'selective'})} className={`flex-1 px-3 py-2 rounded-lg text-xs border ${form.candidate_selection_mode === 'selective' ? 'bg-slate-900 dark:bg-white text-white dark:text-black border-slate-900 dark:border-white' : 'bg-slate-50 dark:bg-zinc-900 text-slate-600 dark:text-zinc-300 border-slate-200 dark:border-zinc-800'}`} data-testid="mode-selective-btn">Selective (pick later)</button>
                  <button type="button" onClick={() => setForm({...form, candidate_selection_mode: 'auto'})} className={`flex-1 px-3 py-2 rounded-lg text-xs border ${form.candidate_selection_mode === 'auto' ? 'bg-slate-900 dark:bg-white text-white dark:text-black border-slate-900 dark:border-white' : 'bg-slate-50 dark:bg-zinc-900 text-slate-600 dark:text-zinc-300 border-slate-200 dark:border-zinc-800'}`} data-testid="mode-auto-btn">Auto-start all matches</button>
                </div>
                <p className="text-slate-500 dark:text-zinc-500 text-[10px] mt-1.5">Each candidate journey costs <span className="text-amber-700 dark:text-amber-300">{pricing?.candidate_journey || 3} MP</span>. Auto mode charges from your set budget below.</p>
                {form.candidate_selection_mode === 'auto' && (
                  <div className="mt-3 space-y-2" data-testid="auto-budget-block">
                    <Inp
                      label={`Auto-interview budget cap (MP) — required`}
                      value={form.auto_interview_budget_mp}
                      onChange={v => setForm({ ...form, auto_interview_budget_mp: v })}
                      type="number"
                      required
                    />
                    <p className="text-[11px] text-slate-500 dark:text-zinc-500" data-testid="auto-budget-hint">
                      {form.auto_interview_budget_mp
                        ? `≈ ${Math.max(0, Math.floor(Number(form.auto_interview_budget_mp) / Math.max(1, (pricing?.candidate_journey || 3))))} candidates will be auto-interviewed at ${pricing?.candidate_journey || 3} MP each. Once the budget is reached, remaining candidates wait until you click Continue.`
                        : `Set an upper cap on total MP spent auto-scheduling AI interviews for this job.`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm" data-testid="save-job-btn">{saving ? 'Saving...' : (job ? 'Save' : `Post (${cost} MP)`)}</button>
          </div>
        </form>
        {!job && (
          <ConfirmChargeDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            title="Post this job?"
            description="Posting will deduct Mu Points from your wallet immediately."
            cost={cost}
            balance={balance}
            confirmLabel="Post job"
            lineItems={[
              { label: 'Job posting', amount: pricing?.job_post || 10 },
              ...(form.recording_addon ? [{ label: 'Recording add-on', amount: pricing?.recording_addon || 5 }] : []),
              ...(form.ai_evaluation_addon ? [{ label: 'AI evaluation add-on', amount: pricing?.ai_eval_addon || 5 }] : []),
            ]}
            onConfirm={doSave}
          />
        )}
        {reopenPrompt && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={() => setReopenPrompt(null)}>
            <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="reopen-prompt-dialog">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Lower the match threshold?</h3>
              <p className="text-slate-500 dark:text-zinc-400 text-sm mb-4">
                You&apos;re changing the minimum match from <span className="font-medium text-slate-800 dark:text-zinc-200">{reopenPrompt.prevThreshold}%</span> to <span className="font-medium text-slate-800 dark:text-zinc-200">{reopenPrompt.newThreshold}%</span>.
                <br /><br />
                <span className="text-emerald-700 dark:text-emerald-300 font-medium">{reopenPrompt.count} previously-rejected {reopenPrompt.count === 1 ? 'candidate' : 'candidates'}</span> now cross the new threshold.
                Would you like to reopen them and put them back into the pipeline?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setReopenPrompt(null); doSave({ reopenRejected: false }).catch(() => { /* noop */ }); }}
                  disabled={saving}
                  className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm"
                  data-testid="reopen-skip-btn">
                  Save without reopening
                </button>
                <button
                  onClick={() => { setReopenPrompt(null); doSave({ reopenRejected: true }).catch(() => { /* noop */ }); }}
                  disabled={saving}
                  className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  data-testid="reopen-confirm-btn">
                  {saving ? 'Saving…' : `Reopen ${reopenPrompt.count} & save`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Inp({ label, value, onChange, type = 'text', textarea, required }) {
  const cls = "w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600";
  return (
    <div>
      <label className="block text-xs text-slate-500 dark:text-zinc-500 mb-1">{label}</label>
      {textarea ? <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} required={required} className={cls} /> : <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} required={required} className={cls} />}
    </div>
  );
}
